import xhr from 'xhr.js';
import auth from 'services/auth';

const integrations = {
    /**
     * Every incentive provider a customer can connect, each carrying whether this
     * account has it connected. Drives the integrations tiles, the payment-method
     * options and the per-study reward selects, so a new provider needs no frontend
     * change.
     *
     * @returns {Promise<Array<Object>>} provider manifest entries
     */
    getIncentiveProviders: async () => {
        const account_id = auth.getAccountId();
        const res = await xhr.get(`/integrations/incentive-providers?account_id=${account_id}`, {
            withCredentials: true
        });
        return res.data;
    },
    /**
     * Store the account's details for a credentials-form provider.
     *
     * @param {string} provider Provider machine name
     * @param {Object} fields Values keyed by the provider's declared field names
     * @returns {Promise<Array<Object>>} refreshed provider manifest
     */
    connectIncentiveProvider: async (provider, fields) => {
        const account_id = auth.getAccountId();
        const res = await xhr.post(
            `/integrations/incentive-providers/${provider}/connect?account_id=${account_id}`,
            fields,
            {
                withCredentials: true
            }
        );
        return res.data;
    },
    /**
     * @param {string} provider Provider machine name
     * @returns {Promise<Array<Object>>} refreshed provider manifest
     */
    disconnectIncentiveProvider: async provider => {
        const account_id = auth.getAccountId();
        const res = await xhr.post(
            `/integrations/incentive-providers/${provider}/disconnect?account_id=${account_id}`,
            {},
            { withCredentials: true }
        );
        return res.data;
    },
    /**
     * Funding accounts a study can pay from, for a provider whose descriptor sets
     * funding_account_per_study.
     *
     * @param {string} provider Provider machine name
     * @returns {Promise<Array<{id: string, name: string}>>}
     */
    getIncentiveProviderFundingAccounts: async provider => {
        const account_id = auth.getAccountId();
        const res = await xhr.get(
            `/integrations/incentive-providers/${provider}/funding-accounts?account_id=${account_id}`,
            { withCredentials: true }
        );
        return res.data;
    },
    /**
     * The funding account a payout from this study through a provider will draw on:
     * the study's own choice, or the provider's only account when there is nothing to
     * choose. `funding_account` is null with a customer-facing `reason` when neither
     * applies.
     *
     * @param {number|string} studyId Study id
     * @param {string} provider Provider machine name
     * @returns {Promise<{funding_account: {id: string, name: string, balance: string|null, currency_code: string}|null, reason: string|null}>}
     */
    getStudyIncentiveFundingAccount: async (studyId, provider) => {
        const account_id = auth.getAccountId();
        const res = await xhr.get(
            `/studies/${studyId}/incentive-providers/${provider}/funding-account?account_id=${account_id}`,
            { withCredentials: true }
        );
        return res.data;
    },
    /**
     * Rewards a study can pay from through a provider.
     *
     * @param {string} provider Provider machine name
     * @returns {Promise<Array<{id: string, name: string}>>}
     */
    getIncentiveProviderTargets: async provider => {
        const account_id = auth.getAccountId();
        const res = await xhr.get(`/integrations/incentive-providers/${provider}/targets?account_id=${account_id}`, {
            withCredentials: true
        });
        return res.data;
    },
    createZoomToken: async code => {
        const account_id = auth.getAccountId();
        const res = await xhr.post(
            `/integrations/zoom/token?account_id=${account_id}`,
            {},
            {
                withCredentials: true,
                params: {
                    code
                }
            }
        );
        return res.data;
    },
    disconnectZoomAccount: async () => {
        const account_id = auth.getAccountId();
        const res = await xhr.post(
            `/integrations/zoom/disconnect?account_id=${account_id}`,
            {},
            {
                withCredentials: true
            }
        );
        return res.data;
    },
    disconnectZoomUser: async () => {
        const account_id = auth.getAccountId();
        const res = await xhr.post(
            `/integrations/zoom/disconnectUser?account_id=${account_id}`,
            {},
            {
                withCredentials: true
            }
        );
        return res.data;
    },
    disconnectCalendlyAccount: async () => {
        const account_id = auth.getAccountId();
        const res = await xhr.post(
            `/integrations/calendly/disconnect?account_id=${account_id}`,
            {},
            {
                withCredentials: true
            }
        );
        return res.data;
    },
    getSalesforceAuthRedirectUrl: async domain => {
        const account_id = auth.getAccountId();
        const res = await xhr.get(
            `/integrations/salesforce/authorize-redirect?account_id=${account_id}&domain=${domain}`,
            {
                withCredentials: true
            }
        );
        return res.data;
    },
    disconnectSalesforceAccount: async () => {
        const account_id = auth.getAccountId();
        const res = await xhr.post(
            `/integrations/salesforce/disconnect?account_id=${account_id}`,
            {},
            {
                withCredentials: true
            }
        );
        return res.data;
    },
    getSalesforceProperties: async () => {
        const account_id = auth.getAccountId();
        const res = await xhr.get(`/integrations/salesforce/contact-properties?account_id=${account_id}`, {
            withCredentials: true
        });
        return res.data;
    },
    /**
     * Map Salesforce properties to Private Panels properties
     *
     * @param {Object} body
     * @param {Object} body.map The mapping of Salesforce properties to Private Panels properties
     * @param {Object} params
     * @param {string} params.panelId The panel ID
     * @param {string} params.salesforceListviewId The Salesforce listview ID
     */
    mapSalesforceToPanelfox: async ({ map }, { panelId, salesforceListviewId }) => {
        const account_id = auth.getAccountId();
        const res = await xhr.post(
            `/integrations/salesforce/map-properties`,
            {
                map
            },
            {
                withCredentials: true,
                params: {
                    account_id,
                    salesforce_listview_id: salesforceListviewId,
                    panel_id: panelId
                }
            }
        );
        return res.data;
    },
    runSalesforceSync: async () => {
        const account_id = auth.getAccountId();
        const res = await xhr.get(`/integrations/salesforce/run?account_id=${account_id}`, {
            withCredentials: true
        });
        return res.data;
    },
    /**
     * Get Salesforce listviews
     */
    getSaleforceListviews: async () => {
        const accountId = auth.getAccountId();
        const res = await xhr.get('/integrations/salesforce/listviews', {
            withCredentials: true,
            params: {
                account_id: accountId
            }
        });
        return res.data;
    },
    /**
     * Get Salesforce integration info
     */
    getSaleforceIntegrationInfo: async () => {
        const accountId = auth.getAccountId();
        const res = await xhr.get('/integrations/salesforce/info', {
            withCredentials: true,
            params: {
                account_id: accountId
            }
        });
        return res.data;
    },
    /**
     * Connect Qualtrics account
     */
    connectQualtrics: async (
        qualtricsApiToken,
        qualtricsDatacenterId,
        qualtricsOrganizationId,
        qualtricsUiDatacenterId
    ) => {
        const account_id = auth.getAccountId();
        const res = await xhr.post(
            `/integrations/qualtrics/connect`,
            {},
            {
                withCredentials: true,
                params: {
                    account_id,
                    qualtrics_api_token: qualtricsApiToken,
                    qualtrics_datacenter_id: qualtricsDatacenterId,
                    qualtrics_organization_id: qualtricsOrganizationId,
                    qualtrics_ui_datacenter_id: qualtricsUiDatacenterId
                }
            }
        );
        return res.data;
    },
    /**
     * Update Qualtrics account
     */
    updateQualtrics: async (qualtricsDatacenterId, qualtricsOrganizationId, qualtricsUiDatacenterId) => {
        const account_id = auth.getAccountId();
        const res = await xhr.post(
            `/integrations/qualtrics/update`,
            {},
            {
                withCredentials: true,
                params: {
                    account_id,
                    qualtrics_datacenter_id: qualtricsDatacenterId,
                    qualtrics_organization_id: qualtricsOrganizationId,
                    qualtrics_ui_datacenter_id: qualtricsUiDatacenterId
                }
            }
        );
        return res.data;
    },
    /**
     * Disconnect Qualtrics account
     */
    disconnectQualtrics: async () => {
        const account_id = auth.getAccountId();
        const res = await xhr.post(
            `/integrations/qualtrics/disconnect?account_id=${account_id}`,
            {},
            {
                withCredentials: true
            }
        );
        return res.data;
    },
    /**
     * Get Qualtrics surveys
     */
    getQualtricsSurveys: async () => {
        const account_id = auth.getAccountId();
        const res = await xhr.get(`/integrations/qualtrics/surveys?account_id=${account_id}`, {
            withCredentials: true
        });
        return res.data;
    }
};

export default integrations;
